import type { SimpleSeason } from './EnvironmentState';

/**
 * Season and calendar math (spec §5.3). Provides day-of-year conversion and a continuous blend
 * between the four seasonal anchors so dragging a date never produces a hard visual swap.
 */

export const DAYS_IN_YEAR = 365;

const CUMULATIVE_DAYS = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** Convert a calendar month (1–12) and day into a day of year (1–365, non-leap). */
export function dayOfYearFromDate(month: number, day: number): number {
  const m = Math.min(12, Math.max(1, Math.trunc(month)));
  const maxDay = MONTH_LENGTHS[m - 1] ?? 31;
  const d = Math.min(maxDay, Math.max(1, Math.trunc(day)));
  return (CUMULATIVE_DAYS[m - 1] ?? 0) + d;
}

/** Convert a day of year (1–365) back into a calendar month/day. */
export function dateFromDayOfYear(dayOfYear: number): { month: number; day: number } {
  const doy = wrapDay(dayOfYear);
  let month = 12;
  for (let i = 0; i < 12; i++) {
    if (doy <= (CUMULATIVE_DAYS[i] ?? 0) + (MONTH_LENGTHS[i] ?? 31)) {
      month = i + 1;
      break;
    }
  }
  return { month, day: doy - (CUMULATIVE_DAYS[month - 1] ?? 0) };
}

/** Wrap any day value into the 1–365 range. */
export function wrapDay(dayOfYear: number): number {
  const d = Math.trunc(dayOfYear);
  return ((d - 1) % DAYS_IN_YEAR + DAYS_IN_YEAR) % DAYS_IN_YEAR + 1;
}

/**
 * The window in which snow and ice are offered: 1 November through 15 March, wrapping the year end.
 *
 * Deliberately wider than the "winter" the season blend describes. That blend puts winter between
 * two anchors 90 days apart and is the right model for *foliage*, where the question is how far
 * through the bracket a plant is; this is a different question — whether it can freeze here — and the
 * honest answer for a temperate northern garden runs from the first hard frost in November to the
 * last one in March, which is most of five months.
 *
 * A fixed window rather than one derived from latitude: latitude in this app drives the sun, and a
 * site at 60° would otherwise get snow into May and quietly change what a saved February view of a
 * site at 35° looked like when the designer moved the pin. Whether it snowed is the designer's call,
 * not the model's — the window only decides when the app offers to ask.
 */
export const FREEZING_WINDOW = { fromDay: 305, toDay: 74 } as const;

/**
 * Whether a day of year falls in {@link FREEZING_WINDOW}. Wraps the year end, so the test is an OR
 * rather than the usual AND.
 */
export function isFreezingSeason(dayOfYear: number): boolean {
  const day = wrapDay(dayOfYear);
  return day >= FREEZING_WINDOW.fromDay || day <= FREEZING_WINDOW.toDay;
}

interface SeasonAnchor {
  season: SimpleSeason;
  day: number;
}

/** Northern-hemisphere seasonal anchors (approximate solstice/equinox days). */
export const SEASON_ANCHORS: readonly SeasonAnchor[] = [
  { season: 'spring', day: 80 },
  { season: 'summer', day: 172 },
  { season: 'autumn', day: 266 },
  { season: 'winter', day: 355 },
];

export interface SeasonBlend {
  from: SimpleSeason;
  to: SimpleSeason;
  /** 0 at the `from` anchor, 1 at the `to` anchor. */
  t: number;
}

/**
 * Continuous blend between the two bracketing season anchors for a day of year. At an anchor the
 * blend resolves exactly to that season (t = 0 of the next bracket), guaranteeing continuity.
 */
export function seasonBlend(dayOfYear: number): SeasonBlend {
  const day = wrapDay(dayOfYear);
  const anchors = SEASON_ANCHORS;

  for (let i = 0; i < anchors.length; i++) {
    const lower = anchors[i];
    const upper = anchors[(i + 1) % anchors.length];
    if (lower === undefined || upper === undefined) continue;
    const lowerDay = lower.day;
    let upperDay = upper.day;
    let d = day;
    if (upperDay <= lowerDay) {
      // Winter → spring wraps past year end.
      upperDay += DAYS_IN_YEAR;
      if (d < lowerDay) d += DAYS_IN_YEAR;
    }
    if (d >= lowerDay && d < upperDay) {
      const span = upperDay - lowerDay;
      const t = span === 0 ? 0 : (d - lowerDay) / span;
      return { from: lower.season, to: upper.season, t };
    }
  }

  // Fallback (shouldn't happen): treat as full winter.
  return { from: 'winter', to: 'spring', t: 0 };
}
